import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, Pencil, X, Lock, CalendarDays, Tag, Users, Briefcase, PiggyBank } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE, Tabs, useUrlTab, Field, FormRow, SectionCard, EmptyState, ErrorState, TableSkeleton, OverflowMenu,
  fmtDate,
} from '../../components/ui';
import { TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, Notice } from './financeKit';

const TABS = [
  { id: 'close', label: 'Close period' },
  { id: 'general', label: 'General' },
  { id: 'multipliers', label: 'Multipliers' },
  { id: 'holidays', label: 'Public holidays' },
  { id: 'owners', label: 'Owners' },
  { id: 'stakeholders', label: 'Stakeholders' },
  { id: 'super', label: 'Superannuation' },
  { id: 'shift-types', label: 'Shift types' },
];

// ── Settings save ────────────────────────────────────────────────────────────
// PUT /financials/settings rewrites all 17 of these columns, and writes the default for any key the
// request leaves out. Each tab used to send only its own keys, so saving Multipliers quietly reset
// the km rates, sleepover rate, geofence and backup email to defaults (and saving General reset the
// multipliers). A save now re-reads what is stored and sends every key, with only this tab's edits
// changed.
//
// Values go as the text that was typed ('0', '62.87'). The server fills a missing value with
// `value || default`, which would turn a number 0 into the default; a typed '0' is kept as 0. An
// empty field goes as null, so the server applies its default — exactly what the hint under the
// field says.
const SETTINGS_KEYS = [
  'fuel_per_km', 'holiday_charge_mult', 'holiday_pay_mult', 'weekend_charge_mult', 'weekend_pay_mult',
  'target_margin_pct', 'shift_start_date', 'backup_email', 'sleepover_rate', 'geofence_radius_m',
  'fund_critical_pct', 'fund_high_pct', 'clock_in_before_min', 'clock_in_after_min',
  'anomaly_hours_threshold', 'default_transport_km_rate', 'km_rate_modified',
];

const asText = (v) => (v == null ? '' : String(v));

async function saveSettings(patch) {
  const { data } = await api.get('/financials/settings');
  const body = {};
  for (const k of SETTINGS_KEYS) {
    const v = data?.[k];
    body[k] = v == null || v === '' ? null : (typeof v === 'number' ? String(v) : v);
  }
  Object.assign(body, patch);
  const r = await api.put('/financials/settings', body);
  return r.data || {};
}

// What was typed, as the server should store it: empty → null (server default), else the number
// written plainly ('0.990' → '0.99').
const toStored = (v) => (String(v ?? '').trim() === '' ? null : String(Number(v)));

function checkNum(v, spec) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return 'Enter a number.';
  if (spec.int && !Number.isInteger(n)) return 'Enter a whole number.';
  if (spec.gt0 && n <= 0) return spec.minMsg || 'Enter a number above 0.';
  if (spec.min != null && n < spec.min) return spec.minMsg || `Enter ${spec.min} or more.`;
  if (spec.max != null && n > spec.max) return `Enter ${spec.max} or less.`;
  return null;
}

// A number field (declared once, outside any render, so typing never remounts it and focus stays).
function NumField({ name, spec, value, onChange, error }) {
  const zero = spec.zeroHint && String(value).trim() !== '' && Number(value) === 0;
  const hint = zero ? <span className="text-amber-700">{spec.zeroHint}</span> : spec.hint;
  return (
    <Field label={spec.label} hint={hint} error={error}>
      {(id) => (
        <input id={id} name={name} type="number" inputMode="decimal" step={spec.step || 'any'} min={spec.min} max={spec.max}
          value={value} onChange={(e) => onChange(name, e.target.value)} placeholder={spec.placeholder}
          className="input-field tabular-nums" aria-invalid={error ? 'true' : undefined} />
      )}
    </Field>
  );
}

// The bar that keeps Save in reach on a long form, and says whether anything is unsaved.
function SaveBar({ dirty, saving, invalid, onSave, onDiscard }) {
  return (
    <div className="sticky bottom-3 z-20">
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-card-deep">
        <span className={clsx('text-sm', invalid ? 'text-rose-700' : dirty ? 'text-slate-800 font-medium' : 'text-slate-500')}>
          {invalid ? 'Fix the fields marked in red to save' : dirty ? 'Unsaved changes' : 'All changes saved'}
        </span>
        <div className="ml-auto flex items-center gap-2">
          {dirty && <button type="button" onClick={onDiscard} disabled={saving} className="btn-secondary">Discard</button>}
          <button type="button" onClick={onSave} disabled={!dirty || saving || invalid} className="btn-primary">
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Warn before leaving the page with edits that have not been saved.
function useUnsavedGuard(dirty) {
  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);
}

// A settings form over some of the keys: load, edit, validate, save through saveSettings().
function useSettingsForm(fields, onDirtyChange) {
  const [form, setForm] = useState(null);
  const [base, setBase] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const fill = useCallback((d) => {
    const next = {};
    for (const k of Object.keys(fields)) {
      next[k] = fields[k].type === 'date' ? asText(d?.[k]).slice(0, 10) : asText(d?.[k]);
    }
    setForm(next); setBase(next);
  }, [fields]);

  const load = useCallback(() => {
    setLoadError(null);
    api.get('/financials/settings').then(r => fill(r.data || {}))
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load these settings. Check your connection, then try again."));
  }, [fill]);
  useEffect(() => { load(); }, [load]);

  const dirty = !!form && !!base && Object.keys(fields).some(k => form[k] !== base[k]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useUnsavedGuard(dirty);

  const errors = {};
  if (form) for (const [k, spec] of Object.entries(fields)) {
    if (spec.type === 'date' || spec.type === 'email') continue;
    const err = checkNum(form[k], spec);
    if (err) errors[k] = err;
  }
  if (form && fields.backup_email && form.backup_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.backup_email.trim())) {
    errors.backup_email = 'Enter a valid email address, or leave it empty.';
  }
  const invalid = Object.keys(errors).length > 0;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (successMsg) => {
    if (invalid) return;
    setSaving(true);
    try {
      const patch = {};
      for (const [k, spec] of Object.entries(fields)) {
        if (spec.type === 'date' || spec.type === 'email') patch[k] = form[k] ? form[k].trim() : null;
        else patch[k] = toStored(form[k]);
      }
      const saved = await saveSettings(patch);
      fill(saved);
      toast.success(successMsg);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return { form, set, errors, invalid, dirty, saving, save, discard: () => setForm(base), loadError, reload: load };
}

// ── General tab ──────────────────────────────────────────────────────────────
// Hints say what an empty field means; the amber note says what the app actually does at 0, because
// most of these are read as "0 = not set, use the default" when shifts are paid, billed and checked.
const GENERAL_FIELDS = {
  sleepover_rate: { label: 'Sleepover rate ($ per sleepover)', hint: 'Flat amount paid for each sleepover shift. Empty uses $62.87.', step: '0.01', min: 0, placeholder: '62.87',
    zeroHint: 'At $0, pay and reports use the default sleepover rate of $62.87.' },
  target_margin_pct: { label: 'Target profit margin (%)', hint: 'Shifts below this margin appear in the rates audit. Empty uses 20%.', step: '0.1', min: 0, max: 100, placeholder: '20',
    zeroHint: 'At 0%, the rates audit uses the default target of 20%.' },
  shift_start_date: { type: 'date', label: 'Shift start date', hint: 'Earliest shift date to include in recalculations.' },
  backup_email: { type: 'email', label: 'Backup email', hint: 'Receives financial alerts and audit notifications.' },
  fund_high_pct: { label: 'High-risk threshold (%)', hint: "Fund usage that counts as 'High' risk on the fund balances report. Empty uses 80%.", step: '1', min: 0, max: 100, placeholder: '80',
    zeroHint: 'At 0%, the fund report uses the default of 80%.' },
  fund_critical_pct: { label: 'Critical-risk threshold (%)', hint: "Fund usage that counts as 'Critical' risk. Empty uses 90%.", step: '1', min: 0, max: 100, placeholder: '90',
    zeroHint: 'At 0%, the fund report uses the default of 90%.' },
  geofence_radius_m: { label: 'Geofence radius (metres)', hint: 'How far from the client address a clock-in still counts as on site. Empty uses 400 m.', step: '10', min: 50, int: true, placeholder: '400',
    minMsg: 'Enter at least 50 m, or leave it empty for the default of 400 m.' },
  clock_in_before_min: { label: 'Clock-in opens (minutes before the shift)', hint: 'How early staff can clock in. Empty uses 60.', step: '5', min: 0, int: true, placeholder: '60',
    zeroHint: 'At 0, clock-in uses the default of 60 minutes.' },
  clock_in_after_min: { label: 'Clock-in closes (minutes after the start)', hint: 'How late staff can still clock in. Empty uses 240.', step: '5', min: 0, int: true, placeholder: '240',
    zeroHint: 'At 0, clock-in uses the default of 240 minutes.' },
  anomaly_hours_threshold: { label: 'Shift length anomaly threshold (hours)', hint: 'Timesheets longer than this are flagged as anomalies. Empty uses 16.', step: '0.5', min: 1, placeholder: '16',
    minMsg: 'Enter 1 hour or more, or leave it empty for the default of 16.' },
  default_transport_km_rate: { label: 'Client charge, normal vehicle ($/km)', hint: 'Empty uses $0.99/km.', step: '0.01', min: 0, placeholder: '0.99',
    zeroHint: 'At $0, mileage is charged at the default of $0.99/km.' },
  km_rate_modified: { label: 'Client charge, modified vehicle ($/km)', hint: 'Higher rate for wheelchair-accessible or modified vehicles. Empty uses $0.99/km.', step: '0.01', min: 0, placeholder: '0.99',
    zeroHint: 'At $0, modified-vehicle trips are charged at the normal-vehicle rate.' },
  fuel_per_km: { label: 'Worker reimbursement ($/km)', hint: 'Paid to workers who use their own car (ATO cents per km).', step: '0.01', min: 0, placeholder: '0.85',
    zeroHint: 'At $0, payslips and generated invoices use the default of $0.85/km.' },
};

function SettingsTab({ onDirtyChange }) {
  const f = useSettingsForm(GENERAL_FIELDS, onDirtyChange);
  if (f.loadError) return <div className="card"><ErrorState message={f.loadError} onRetry={f.reload} /></div>;
  if (!f.form) return <div className="card"><TableSkeleton rows={6} cols={2} /></div>;
  const nf = (k) => <NumField name={k} spec={GENERAL_FIELDS[k]} value={f.form[k]} onChange={f.set} error={f.errors[k]} />;
  return (
    <div className="space-y-4">
      <SectionCard title="General" bodyClass="p-5">
        <FormRow cols={2}>
          {nf('sleepover_rate')}
          {nf('target_margin_pct')}
          <Field label={GENERAL_FIELDS.shift_start_date.label} hint={GENERAL_FIELDS.shift_start_date.hint}>
            {(id) => <input id={id} type="date" value={f.form.shift_start_date} onChange={e => f.set('shift_start_date', e.target.value)} className="input-field" />}
          </Field>
          <Field label={GENERAL_FIELDS.backup_email.label} hint={GENERAL_FIELDS.backup_email.hint} error={f.errors.backup_email}>
            {(id) => <input id={id} type="email" value={f.form.backup_email} onChange={e => f.set('backup_email', e.target.value)} className="input-field" placeholder="finance@example.com" />}
          </Field>
        </FormRow>
      </SectionCard>

      <SectionCard title="Fund thresholds" bodyClass="p-5">
        <FormRow cols={2}>
          {nf('fund_high_pct')}
          {nf('fund_critical_pct')}
        </FormRow>
      </SectionCard>

      <SectionCard title="Mileage rates" bodyClass="p-5">
        <FormRow cols={2}>
          {nf('default_transport_km_rate')}
          {nf('km_rate_modified')}
          {nf('fuel_per_km')}
        </FormRow>
      </SectionCard>

      <SectionCard title="Clock-in and attendance" bodyClass="p-5">
        <FormRow cols={2}>
          {nf('geofence_radius_m')}
          {nf('anomaly_hours_threshold')}
          {nf('clock_in_before_min')}
          {nf('clock_in_after_min')}
        </FormRow>
      </SectionCard>

      <SaveBar dirty={f.dirty} saving={f.saving} invalid={f.invalid} onDiscard={f.discard} onSave={() => f.save('Settings saved')} />
    </div>
  );
}

// ── Multipliers tab ───────────────────────────────────────────────────────────
const MULT_FIELDS = {
  holiday_charge_mult: { label: 'Public holiday charge multiplier', hint: 'Charge rate = standard × this, when no public holiday rate is set. Empty uses 2.0.', step: '0.01', gt0: true, max: 10, placeholder: '2.0',
    minMsg: 'Enter a multiplier above 0, or leave it empty for the default of 2.0.' },
  holiday_pay_mult: { label: 'Public holiday pay multiplier', hint: 'Pay rate = standard × this, when no public holiday rate is set. Empty uses 2.0.', step: '0.01', gt0: true, max: 10, placeholder: '2.0',
    minMsg: 'Enter a multiplier above 0, or leave it empty for the default of 2.0.' },
  weekend_charge_mult: { label: 'Weekend charge multiplier', hint: 'Charge rate = standard × this, when no Saturday or Sunday rate is set. Empty uses 1.5.', step: '0.01', gt0: true, max: 10, placeholder: '1.5',
    minMsg: 'Enter a multiplier above 0, or leave it empty for the default of 1.5.' },
  weekend_pay_mult: { label: 'Weekend pay multiplier', hint: 'Pay rate = standard × this, when no Saturday or Sunday rate is set. Empty uses 1.5.', step: '0.01', gt0: true, max: 10, placeholder: '1.5',
    minMsg: 'Enter a multiplier above 0, or leave it empty for the default of 1.5.' },
};

function MultipliersTab({ onDirtyChange }) {
  const f = useSettingsForm(MULT_FIELDS, onDirtyChange);
  if (f.loadError) return <div className="card"><ErrorState message={f.loadError} onRetry={f.reload} /></div>;
  if (!f.form) return <div className="card"><TableSkeleton rows={4} cols={2} /></div>;
  return (
    <div className="space-y-4">
      <Notice tone="slate">
        Multipliers are a <strong>fallback</strong>: they apply only when a shift has no specific Saturday, Sunday or public holiday rate in client or staff rates. An explicit rate always wins.
      </Notice>
      <SectionCard title="Rate multipliers" bodyClass="p-5">
        <FormRow cols={2}>
          {Object.keys(MULT_FIELDS).map(k => (
            <NumField key={k} name={k} spec={MULT_FIELDS[k]} value={f.form[k]} onChange={f.set} error={f.errors[k]} />
          ))}
        </FormRow>
      </SectionCard>
      <SaveBar dirty={f.dirty} saving={f.saving} invalid={f.invalid} onDiscard={f.discard} onSave={() => f.save('Multipliers saved')} />
    </div>
  );
}

// ── Public Holidays tab ───────────────────────────────────────────────────────
function PublicHolidaysTab() {
  const confirm = useConfirm();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', holiday_date: '', state: '' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadHols = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/financials/public-holidays'); setHolidays(r.data || []); }
    catch (e) { setError(e.response?.data?.message || "We couldn't load public holidays."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadHols(); }, [loadHols]);

  async function save() {
    if (!form.name || !form.holiday_date) return toast.error('Name and date required');
    setSaving(true);
    try {
      await api.post('/financials/public-holidays', form);
      toast.success('Holiday added');
      setForm({ name: '', holiday_date: '', state: '' });
      setShowForm(false);
      loadHols();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add'); }
    finally { setSaving(false); }
  }

  async function del(h) {
    if (!await confirm('Shifts on this date are no longer treated as a public holiday when they are next priced.', {
      title: `Delete ${h.name} (${fmtDate(String(h.holiday_date).slice(0, 10))})?`, confirmLabel: 'Delete holiday', danger: true,
    })) return;
    try {
      await api.delete(`/financials/public-holidays/${h.id}`);
      toast.success('Holiday deleted');
      loadHols();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => setShowForm(s => !s)} className="btn-primary"><Plus size={14} /> Add holiday</button>
      </div>
      {showForm && (
        <SectionCard title="New public holiday" bodyClass="p-5"
          actions={<button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={15} /></button>}>
          <FormRow cols={3}>
            <Field label="Name" required>
              {(id) => <input id={id} type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input-field" placeholder="e.g. Christmas Day" />}
            </Field>
            <Field label="Date" required>
              {(id) => <input id={id} type="date" value={form.holiday_date} onChange={e => setForm(f => ({ ...f, holiday_date: e.target.value }))} className="input-field" />}
            </Field>
            <Field label="State" hint="Optional">
              {(id) => <input id={id} type="text" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} className="input-field" placeholder="e.g. NSW" />}
            </Field>
          </FormRow>
          <div className="flex justify-end mt-4">
            <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Add holiday'}</button>
          </div>
        </SectionCard>
      )}
      <div className="card overflow-hidden">
        {error ? <ErrorState message={error} onRetry={loadHols} /> : loading ? <TableSkeleton rows={4} cols={3} /> : holidays.length === 0 ? (
          <EmptyState icon={CalendarDays} title="No public holidays yet" hint="Add the public holidays in your state so shifts on those days are priced at public holiday rates." />
        ) : (
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead><tr className={THEAD_ROW}>
                <th scope="col" className={TH}>Name</th><th scope="col" className={TH}>Date</th><th scope="col" className={TH}>State</th>
                <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {holidays.map(h => (
                  <tr key={h.id} className={TR}>
                    <td className={`${TD} font-medium text-slate-800`}>{h.name}</td>
                    <td className={`${TD} whitespace-nowrap`}>{fmtDate(String(h.holiday_date || '').slice(0, 10))}</td>
                    <td className={`${TD} text-slate-600`}>{h.state || '—'}</td>
                    <td className={`${TD} text-right`}>
                      <button type="button" onClick={() => del(h)} aria-label={`Delete ${h.name}`}
                        className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Generic split party tab (owners / stakeholders) ───────────────────────────
function SplitPartyTab({ endpoint, label, singular, icon: Icon }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm]   = useState({ name: '', percentage: '' });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get(`/financials/${endpoint}`); setItems(r.data || []); }
    catch (e) { setError(e.response?.data?.message || `We couldn't load ${label.toLowerCase()}.`); }
    finally { setLoading(false); }
  }, [endpoint, label]);
  useEffect(() => { load(); }, [load]);

  function openEdit(item) {
    setForm({ name: item.name, percentage: item.percentage });
    setEditing(item.id);
    setShowForm(true);
  }

  async function save() {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      const body = { name: form.name, percentage: parseFloat(form.percentage) || 0 };
      if (editing) { await api.put(`/financials/${endpoint}/${editing}`, body); toast.success('Updated'); }
      else          { await api.post(`/financials/${endpoint}`, body);           toast.success('Added'); }
      setShowForm(false); setEditing(null); setForm({ name: '', percentage: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function del(item) {
    if (!await confirm(`${item.name} no longer receives a share of profit in the reports.`, {
      title: `Delete ${singular.toLowerCase()} ${item.name}?`, confirmLabel: `Delete ${singular.toLowerCase()}`, danger: true,
    })) return;
    try {
      await api.delete(`/financials/${endpoint}/${item.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  }

  const totalPct = items.reduce((s, i) => s + parseFloat(i.percentage || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className={clsx('text-sm px-3 py-1.5 rounded-lg font-medium tabular-nums',
          totalPct > 100 ? 'bg-red-50 text-red-700' : totalPct === 100 ? 'bg-emerald-50 text-emerald-800' : 'bg-slate-100 text-slate-700')}>
          Total: {totalPct.toFixed(2)}%{totalPct !== 100 && totalPct > 0 ? ' (should be 100%)' : ''}
        </span>
        <button type="button" onClick={() => { setForm({ name: '', percentage: '' }); setEditing(null); setShowForm(true); }} className="btn-primary">
          <Plus size={14} /> Add {singular.toLowerCase()}
        </button>
      </div>
      {showForm && (
        <SectionCard title={`${editing ? 'Edit' : 'New'} ${singular.toLowerCase()}`} bodyClass="p-5"
          actions={<button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={15} /></button>}>
          <FormRow cols={2}>
            <Field label="Name" required>
              {(id) => <input id={id} type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input-field" placeholder="e.g. John Smith" />}
            </Field>
            <Field label="Share (%)">
              {(id) => <input id={id} type="number" step="0.01" min="0" max="100" value={form.percentage} onChange={e => setForm(f => ({ ...f, percentage: e.target.value }))} className="input-field tabular-nums" placeholder="50.00" />}
            </Field>
          </FormRow>
          <div className="flex justify-end mt-4">
            <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </SectionCard>
      )}
      <div className="card overflow-hidden">
        {error ? <ErrorState message={error} onRetry={load} /> : loading ? <TableSkeleton rows={3} cols={2} /> : items.length === 0 ? (
          <EmptyState icon={Icon} title={`No ${label.toLowerCase()} yet`} hint={`Add ${label.toLowerCase()} and their share so the profit and stakeholder reports can split profit.`} />
        ) : (
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead><tr className={THEAD_ROW}>
                <th scope="col" className={TH}>Name</th><th scope="col" className={TH_NUM}>Share</th>
                <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id} className={TR}>
                    <td className={`${TD} font-medium text-slate-800`}>{item.name}</td>
                    <td className={TD_NUM}>{Number(item.percentage).toFixed(2)}%</td>
                    <td className={`${TD} text-right`}>
                      <OverflowMenu label={`Actions for ${item.name}`} items={[
                        { label: 'Edit', icon: Pencil, onClick: () => openEdit(item) },
                        { label: 'Delete', icon: Trash2, onClick: () => del(item), danger: true },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Superannuation rates tab ───────────────────────────────────────────────────
function SuperannuationTab() {
  const confirm = useConfirm();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm]   = useState({ percentage: '', start_date: '', end_date: '' });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadRates = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/financials/super-percentages'); setRates(r.data || []); }
    catch (e) { setError(e.response?.data?.message || "We couldn't load super rates."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadRates(); }, [loadRates]);

  async function save() {
    if (!form.percentage || !form.start_date) return toast.error('Percentage and start date required');
    setSaving(true);
    try {
      await api.post('/financials/super-percentages', {
        percentage: parseFloat(form.percentage),
        start_date: form.start_date,
        end_date:   form.end_date || null,
      });
      toast.success('Rate added');
      setForm({ percentage: '', start_date: '', end_date: '' });
      setShowForm(false);
      loadRates();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function del(r) {
    if (!await confirm('Super for shifts in its date range is worked out from the remaining rates when they are next priced.', {
      title: `Delete the ${Number(r.percentage).toFixed(2)}% super rate?`, confirmLabel: 'Delete rate', danger: true,
    })) return;
    try {
      await api.delete(`/financials/super-percentages/${r.id}`);
      toast.success('Rate deleted');
      loadRates();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-slate-600">The superannuation guarantee is <strong>12%</strong> from 1 July 2025.</p>
        <button type="button" onClick={() => setShowForm(s => !s)} className="btn-primary"><Plus size={14} /> Add rate</button>
      </div>
      {showForm && (
        <SectionCard title="New super rate" bodyClass="p-5"
          actions={<button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={15} /></button>}>
          <FormRow cols={3}>
            <Field label="Rate (%)" required>
              {(id) => <input id={id} type="number" step="0.01" min="0" max="30" value={form.percentage} onChange={e => setForm(f => ({ ...f, percentage: e.target.value }))} className="input-field tabular-nums" placeholder="12" />}
            </Field>
            <Field label="Start date" required>
              {(id) => <input id={id} type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="input-field" />}
            </Field>
            <Field label="End date" hint="Leave empty if it is current">
              {(id) => <input id={id} type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="input-field" />}
            </Field>
          </FormRow>
          <div className="flex justify-end mt-4">
            <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Add rate'}</button>
          </div>
        </SectionCard>
      )}
      <div className="card overflow-hidden">
        {error ? <ErrorState message={error} onRetry={loadRates} /> : loading ? <TableSkeleton rows={3} cols={3} /> : rates.length === 0 ? (
          <EmptyState icon={PiggyBank} title="No super rates yet" hint="Add the superannuation guarantee rate so staff cost includes super." />
        ) : (
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead><tr className={THEAD_ROW}>
                <th scope="col" className={TH_NUM}>Rate</th><th scope="col" className={TH}>From</th><th scope="col" className={TH}>To</th>
                <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {rates.map(r => (
                  <tr key={r.id} className={TR}>
                    <td className={`${TD_NUM} font-semibold`}>{Number(r.percentage).toFixed(2)}%</td>
                    <td className={`${TD} whitespace-nowrap`}>{fmtDate(String(r.start_date || '').slice(0, 10))}</td>
                    <td className={`${TD} whitespace-nowrap text-slate-600`}>{r.end_date ? fmtDate(String(r.end_date).slice(0, 10)) : 'Current'}</td>
                    <td className={`${TD} text-right`}>
                      <button type="button" onClick={() => del(r)} aria-label={`Delete the ${Number(r.percentage).toFixed(2)}% rate`}
                        className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shift Types tab ───────────────────────────────────────────────────────────
const TYPE_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16','#f97316','#14b8a6'];
const COLOUR_NAMES = ['Indigo', 'Green', 'Amber', 'Red', 'Violet', 'Cyan', 'Pink', 'Lime', 'Orange', 'Teal'];

function ShiftTypesTab() {
  const confirm = useConfirm();
  const [types, setTypes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm]     = useState({ name: '', color: TYPE_COLORS[0], service_type: '' });
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTypes = useCallback(async () => {
    setLoading(true); setError(null);
    try { const r = await api.get('/financials/shift-types'); setTypes(r.data || []); }
    catch (e) { setError(e.response?.data?.message || "We couldn't load shift types."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadTypes(); }, [loadTypes]);

  function openEdit(t) {
    setForm({ name: t.name, color: t.color || TYPE_COLORS[0], service_type: t.service_type || '' });
    setEditing(t.id);
    setShowForm(true);
  }

  async function save() {
    if (!form.name) return toast.error('Name required');
    setSaving(true);
    try {
      const body = { name: form.name, color: form.color, service_type: form.service_type || null };
      if (editing) { await api.put(`/financials/shift-types/${editing}`, body); toast.success('Updated'); }
      else          { await api.post('/financials/shift-types', body);           toast.success('Added'); }
      setShowForm(false); setEditing(null); setForm({ name: '', color: TYPE_COLORS[0], service_type: '' });
      loadTypes();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  }

  async function del(t) {
    if (!await confirm('Shifts already using it keep their type name.', {
      title: `Delete the shift type ${t.name}?`, confirmLabel: 'Delete shift type', danger: true,
    })) return;
    try {
      await api.delete(`/financials/shift-types/${t.id}`);
      toast.success('Deleted');
      loadTypes();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to delete'); }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button type="button" onClick={() => { setForm({ name: '', color: TYPE_COLORS[0], service_type: '' }); setEditing(null); setShowForm(true); }} className="btn-primary">
          <Plus size={14} /> Add shift type
        </button>
      </div>
      {showForm && (
        <SectionCard title={`${editing ? 'Edit' : 'New'} shift type`} bodyClass="p-5"
          actions={<button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><X size={15} /></button>}>
          <FormRow cols={3}>
            <Field label="Name" required>
              {(id) => <input id={id} type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="input-field" placeholder="e.g. Community access" />}
            </Field>
            <Field label="Service type">
              {(id) => <input id={id} type="text" value={form.service_type} onChange={e => setForm(f => ({ ...f, service_type: e.target.value }))} className="input-field" placeholder="e.g. NDIS, Private" />}
            </Field>
            <div>
              <span className="text-[13px] font-medium text-slate-700 mb-1 block" id="st-colour-label">Colour</span>
              <div className="flex flex-wrap gap-1.5 mt-1" role="group" aria-labelledby="st-colour-label">
                {TYPE_COLORS.map((c, i) => (
                  <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                    aria-label={COLOUR_NAMES[i]} aria-pressed={form.color === c}
                    className={clsx('w-8 h-8 rounded-full border-2 transition-transform duration-150', form.color === c ? 'border-slate-800 scale-105' : 'border-white ring-1 ring-slate-200')}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </FormRow>
          <div className="flex justify-end mt-4">
            <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </SectionCard>
      )}
      <div className="card overflow-hidden">
        {error ? <ErrorState message={error} onRetry={loadTypes} /> : loading ? <TableSkeleton rows={4} cols={2} /> : types.length === 0 ? (
          <EmptyState icon={Tag} title="No shift types yet" hint="Shift types group shifts in the financial reports and travel settings." />
        ) : (
          <div className={TABLE_WRAP}>
            <table className={TABLE}>
              <thead><tr className={THEAD_ROW}>
                <th scope="col" className={TH}>Name</th><th scope="col" className={TH}>Service type</th>
                <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
              </tr></thead>
              <tbody>
                {types.map(t => (
                  <tr key={t.id} className={TR}>
                    <td className={`${TD} font-medium text-slate-800`}>
                      <span className="inline-block w-2.5 h-2.5 rounded-full mr-2 align-middle" style={{ background: t.color || '#6366f1' }} aria-hidden="true" />
                      {t.name}
                    </td>
                    <td className={`${TD} text-slate-600`}>{t.service_type || '—'}</td>
                    <td className={`${TD} text-right`}>
                      <OverflowMenu label={`Actions for ${t.name}`} items={[
                        { label: 'Edit', icon: Pencil, onClick: () => openEdit(t) },
                        { label: 'Delete', icon: Trash2, onClick: () => del(t), danger: true },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Close period ──────────────────────────────────────────────────────────────
// Financial close. The backend has honoured fin_settings.period_locked_through for a long time —
// it is what stops a settled fortnight's pay being silently re-priced, a closed month's expenses
// being edited, and an already-reported invoice being reversed — but nothing in the app could
// ever set it. A control that exists only as an API endpoint is a control nobody uses. Locking,
// moving and reopening each ask first: they decide what can still change in the books.
const dayAfter = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const dt = new Date(y, m - 1, d + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

function PeriodLockPanel() {
  const confirm = useConfirm();
  const [lock, setLock] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api.get('/financials/period-lock');
      const v = r.data?.lockedThrough ? String(r.data.lockedThrough).slice(0, 10) : null;
      setLock(v); setDraft(v || '');
    } catch (e) { setLoadError(e.response?.data?.message || "We couldn't load the period lock."); }
    finally { setLoaded(true); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (value) => {
    setBusy(true);
    try {
      await api.post('/financials/period-lock', { lockedThrough: value });
      setLock(value); setDraft(value || '');
      toast.success(value ? `Locked through ${fmtDate(value)}` : 'Period reopened');
    } catch (e) { toast.error(e.response?.data?.message || 'Could not change the lock'); }
    finally { setBusy(false); }
  };

  const lockOrMove = async () => {
    if (!draft || draft === lock) return;
    let opts;
    if (!lock) {
      opts = {
        title: `Lock everything on or before ${fmtDate(draft)}?`,
        body: "Pay won't be re-priced, expenses and invoices dated in that period can't be changed, and approved work in it can't be unapproved. You can reopen it later.",
        confirmLabel: 'Lock period',
      };
    } else if (draft > lock) {
      opts = {
        title: `Extend the lock to ${fmtDate(draft)}?`,
        body: `Everything from ${fmtDate(dayAfter(lock))} to ${fmtDate(draft)} is closed as well: pay won't be re-priced and expenses and invoices dated then can't be changed.`,
        confirmLabel: 'Extend lock',
      };
    } else {
      opts = {
        title: `Move the lock back to ${fmtDate(draft)}?`,
        body: `Everything from ${fmtDate(dayAfter(draft))} to ${fmtDate(lock)} reopens: it can be edited, re-priced and unapproved again, so figures you have already filed for those dates may move.`,
        confirmLabel: 'Move lock back', danger: true,
      };
    }
    if (!await confirm(opts.body, { title: opts.title, confirmLabel: opts.confirmLabel, danger: !!opts.danger })) return;
    save(draft);
  };

  const reopen = async () => {
    if (!await confirm(`Pay can be re-priced again, and expenses and invoices dated on or before ${fmtDate(lock)} can be changed or reversed. Figures you have already filed may move.`, {
      title: `Reopen the period locked through ${fmtDate(lock)}?`, confirmLabel: 'Reopen period', danger: true,
    })) return;
    save(null);
  };

  if (!loaded) return <div className="card"><TableSkeleton rows={2} cols={2} /></div>;
  if (loadError) return <div className="card"><ErrorState message={loadError} onRetry={load} /></div>;
  return (
    <div className={clsx('rounded-xl border p-5', lock ? 'border-amber-200 bg-amber-50/60' : 'border-slate-200 bg-white')}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Lock size={14} className="text-slate-500" /> Financial close</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {lock
              ? <>Everything dated <b>on or before {fmtDate(lock)}</b> is closed. Pay will not be re-priced, expenses and invoices in that period cannot be changed, and approved work cannot be un-approved.</>
              : <>No period is closed. Once you have reported a BAS or finished a pay run, lock it here so a later edit cannot move figures you have already filed.</>}
          </p>
        </div>
        {lock && <span className="badge-yellow whitespace-nowrap">Locked through {fmtDate(lock)}</span>}
      </div>
      <div className="flex items-end gap-3 mt-4 flex-wrap">
        <div>
          <label htmlFor="lock-through" className="block text-[13px] font-medium text-slate-700 mb-1">Lock everything through</label>
          <input id="lock-through" type="date" value={draft} onChange={(e) => setDraft(e.target.value)} className="input-field !w-auto" />
        </div>
        <button type="button" onClick={lockOrMove} disabled={busy || !draft || draft === lock}
          className="btn-primary disabled:!bg-slate-200 disabled:!text-slate-500 disabled:!opacity-100 disabled:!shadow-none">
          {busy ? 'Saving…' : lock ? 'Move lock' : 'Lock period'}
        </button>
        {lock && (
          <button type="button" onClick={reopen} disabled={busy} className="btn-secondary !text-rose-700 !border-rose-300 hover:!bg-rose-50">Reopen</button>
        )}
      </div>
      {!draft && <p className="text-xs text-slate-500 mt-2">Pick a date to lock.</p>}
      {draft && draft === lock && <p className="text-xs text-slate-500 mt-2">Pick a different date to move the lock.</p>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function FinConfig() {
  const confirm = useConfirm();
  const [tab, setTabUrl] = useUrlTab('tab', 'close', TABS.map(t => t.id));
  const [dirty, setDirty] = useState(false);

  // A settings tab with unsaved edits asks before it is left.
  const setTab = async (next) => {
    if (next === tab) return;
    if (dirty && !await confirm('Your changes on this tab have not been saved.', { title: 'Leave without saving?', confirmLabel: 'Discard changes', danger: true })) return;
    setDirty(false);
    setTabUrl(next);
  };

  return (
    <div className={PAGE}>
      {/* The top bar already names the page; the tabs name its screens. */}
      <div>
        <Tabs ariaLabel="Financial configuration" value={tab} onChange={setTab} tabs={TABS} />
        <p className="text-xs text-slate-500 mt-2">
          Rates for the margin reports have their own pages:{' '}
          <Link to="/financials/client-rates" className="font-medium text-primary-700 hover:underline">Client rates</Link>{' · '}
          <Link to="/financials/staff-rates" className="font-medium text-primary-700 hover:underline">Staff rates</Link>
        </p>
      </div>

      {tab === 'close' && <PeriodLockPanel />}
      {tab === 'general' && <SettingsTab onDirtyChange={setDirty} />}
      {tab === 'multipliers' && <MultipliersTab onDirtyChange={setDirty} />}
      {tab === 'holidays' && <PublicHolidaysTab />}
      {tab === 'owners' && <SplitPartyTab endpoint="owners" label="Owners" singular="Owner" icon={Users} />}
      {tab === 'stakeholders' && <SplitPartyTab endpoint="stakeholders" label="Stakeholders" singular="Stakeholder" icon={Briefcase} />}
      {tab === 'super' && <SuperannuationTab />}
      {tab === 'shift-types' && <ShiftTypesTab />}
    </div>
  );
}

